import { Page } from "@/components/page-layout";
import { useCanViewToolLogs } from "@/components/observe/observeDeepLink";
import { RequireScope } from "@/components/require-scope";
import { useProject } from "@/contexts/Auth";
import { useRBAC } from "@/hooks/useRBAC";
import { Badge } from "@/components/ui/Badge";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Text } from "@/components/ui/Text";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";
import { HumanizeDateTime } from "@/lib/dates";
import { useCreateTriggerMutation } from "@gram/client/react-query/createTrigger.js";
import { useDeleteTriggerMutation } from "@gram/client/react-query/deleteTrigger.js";
import { useListEnvironments } from "@gram/client/react-query/listEnvironments.js";
import { invalidateAllTrigger } from "@gram/client/react-query/trigger.js";
import { useTriggerDefinitions } from "@gram/client/react-query/triggerDefinitions.js";
import {
  useTriggers,
  invalidateAllTriggers,
} from "@gram/client/react-query/triggers.js";
import { useUpdateTriggerMutation } from "@gram/client/react-query/updateTrigger.js";
import { TriggerInstance } from "@gram/client/models/components/triggerinstance.js";
import { TriggerDefinition } from "@gram/client/models/components/triggerdefinition.js";
import { CreateTriggerInstanceFormTargetKind as TargetKind } from "@gram/client/models/components/createtriggerinstanceform.js";
import { useRoutes } from "@/routes";
import { SlackReplyModeOptions } from "@/pages/assistants/onboarding/SlackReplyModeOptions";
import {
  SLACK_CONVERSATION_EVENT_TYPES,
  isSlackReplyMode,
} from "@/pages/assistants/onboarding/slackCapabilities";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Stack } from "@/components/ui/Stack";
import { type Column, Table } from "@/components/ui/Table";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Outlet } from "react-router";

export function TriggersRoot(): JSX.Element {
  return <Outlet />;
}

type TriggerConfig = Record<string, unknown>;

type TriggerConfigSchemaProperty = {
  default?: string;
  description?: string;
  items?: {
    enum?: string[];
  };
  title?: string;
  type?: string | string[];
};

type TriggerConfigSchema = {
  properties?: Record<string, TriggerConfigSchemaProperty>;
  required?: string[];
};

type TriggerTargetKindValue = (typeof TargetKind)[keyof typeof TargetKind];

const triggerTargetKindOptions: Array<{
  value: TriggerTargetKindValue;
  label: string;
  description: string;
}> = [
  {
    value: TargetKind.Assistant,
    label: "Assistant",
    description: "Dispatch the trigger to an assistant target.",
  },
  {
    value: TargetKind.Noop,
    label: "Noop sink",
    description:
      "Test sink that records dispatches without executing anything.",
  },
];

function isTriggerTargetKind(value: string): value is TriggerTargetKindValue {
  return triggerTargetKindOptions.some((option) => option.value === value);
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "active":
      return <Badge variant="neutral">Active</Badge>;
    case "fired":
      return <Badge variant="neutral">Fired</Badge>;
    case "cancelled":
      return <Badge variant="neutral">Cancelled</Badge>;
    case "paused":
    default:
      return <Badge variant="neutral">Paused</Badge>;
  }
}

function KindBadge({ kind }: { kind: string }) {
  if (kind === "webhook") {
    return <Badge variant="neutral">Webhook</Badge>;
  }
  return <Badge variant="neutral">Schedule</Badge>;
}

function WebhookUrlPill({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      title={copied ? "Copied!" : url}
      className="group border-border bg-muted/30 hover:border-muted-foreground/40 hover:bg-muted/50 focus-visible:ring-ring inline-flex max-w-[260px] min-w-0 items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <span className="text-foreground shrink-0 font-semibold tracking-wide uppercase">
        URL
      </span>
      <span aria-hidden="true" className="bg-border h-3 w-px shrink-0" />
      <span className="text-muted-foreground truncate font-mono">{url}</span>
      <Icon
        name={copied ? "check" : "copy"}
        className="text-muted-foreground group-hover:text-foreground h-3 w-3 shrink-0"
      />
    </button>
  );
}

function TriggersEmptyState({
  onCreate,
  projectId,
}: {
  onCreate: () => void;
  projectId: string;
}) {
  return (
    <div className="bg-muted/20 flex flex-col items-center justify-center border border-dashed px-8 py-16">
      <div className="bg-muted/50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
        <Icon name="zap" className="text-muted-foreground h-6 w-6" />
      </div>
      <Text variant="subheading" className="mb-1">
        No triggers yet
      </Text>
      <Text small muted className="mb-4 max-w-md text-center">
        Triggers let you connect external events to your assistants. Set up a
        cron schedule or a webhook to get started.
      </Text>
      <RequireScope
        scope="project:write"
        resourceId={projectId}
        level="component"
        reason="You don't have permission to create triggers."
      >
        <Button onClick={onCreate}>
          <Button.LeftIcon>
            <Icon name="plus" className="h-4 w-4" />
          </Button.LeftIcon>
          <Button.Text>Create Trigger</Button.Text>
        </Button>
      </RequireScope>
    </div>
  );
}

function triggerLogsFilterParam(triggerId: string): string {
  return `${encodeURIComponent("gram.trigger.instance_id")}:eq:${encodeURIComponent(triggerId)}`;
}

function TriggersTable({
  triggers,
  definitions,
  onEdit,
}: {
  triggers: TriggerInstance[];
  definitions: TriggerDefinition[];
  onEdit?: (trigger: TriggerInstance) => void;
}) {
  const routes = useRoutes();
  const canViewToolLogs = useCanViewToolLogs();
  const defMap = new Map(definitions.map((d) => [d.slug, d]));
  const columns: Column<TriggerInstance>[] = [
    {
      key: "name",
      header: "Name",
      width: "1fr",
      render: (trigger) => <span className="font-medium">{trigger.name}</span>,
    },
    {
      key: "definitionSlug",
      header: "Type",
      width: "2fr",
      render: (trigger) => {
        const def = defMap.get(trigger.definitionSlug);

        return (
          <div className="flex min-w-0 items-center gap-2">
            <KindBadge kind={def?.kind ?? "webhook"} />
            <span className="text-muted-foreground shrink-0 text-sm">
              {def?.title ?? trigger.definitionSlug}
            </span>
            {def?.kind === "webhook" && trigger.webhookUrl && (
              <WebhookUrlPill url={trigger.webhookUrl} />
            )}
          </div>
        );
      },
    },
    {
      key: "targetDisplay",
      header: "Target",
      width: "1fr",
      render: (trigger) => (
        <span className="text-muted-foreground">{trigger.targetDisplay}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "0.8fr",
      render: (trigger) => <StatusBadge status={trigger.status} />,
    },
    {
      key: "updatedAt",
      header: "Updated",
      width: "1fr",
      render: (trigger) => (
        <span className="text-muted-foreground">
          <HumanizeDateTime date={trigger.updatedAt} />
        </span>
      ),
    },
    // Tool Logs needs log:read on the project, so the shortcut is only
    // offered to those who can open it.
    ...(canViewToolLogs
      ? ([
          {
            key: "logs",
            header: "",
            width: "auto",
            render: (trigger) => (
              <div onClick={(e) => e.stopPropagation()}>
                <routes.logs.Link
                  queryParams={{ af: triggerLogsFilterParam(trigger.id) }}
                  className="text-muted-foreground hover:text-foreground no-underline hover:no-underline"
                >
                  <Icon name="file-text" className="h-4 w-4" />
                </routes.logs.Link>
              </div>
            ),
          },
        ] satisfies Column<TriggerInstance>[])
      : []),
  ];

  return (
    <Table
      columns={columns}
      data={triggers}
      rowKey={(trigger) => trigger.id}
      onRowClick={onEdit}
    />
  );
}

function ConfigField({
  fieldKey,
  prop,
  isRequired,
  config,
  onChange,
}: {
  fieldKey: string;
  prop: TriggerConfigSchemaProperty;
  isRequired: boolean;
  config: TriggerConfig;
  onChange: (config: TriggerConfig) => void;
}) {
  const label = prop.title || fieldKey;
  const description = prop.description;

  const isArrayType =
    prop.type === "array" ||
    (Array.isArray(prop.type) && prop.type.includes("array"));

  if (isArrayType && prop.items?.enum) {
    const options: string[] = prop.items.enum;
    const rawSelected = config[fieldKey];
    const selected = Array.isArray(rawSelected)
      ? rawSelected.filter(
          (value): value is string => typeof value === "string",
        )
      : [];
    const toggle = (val: string) => {
      const next = selected.includes(val)
        ? selected.filter((v: string) => v !== val)
        : [...selected, val];
      onChange({ ...config, [fieldKey]: next });
    };
    return (
      <div>
        <Text variant="body" className="mb-1 font-medium">
          {label}
          {isRequired && " *"}
        </Text>
        {description && (
          <Text small muted className="mb-2">
            {description}
          </Text>
        )}
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={
                selected.includes(opt)
                  ? "border-primary bg-primary/5 border px-3 py-1 text-sm"
                  : "border-border hover:border-muted-foreground/30 border px-3 py-1 text-sm"
              }
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Text variant="body" className="mb-1 font-medium">
        {label}
        {isRequired && " *"}
      </Text>
      {description && (
        <Text small muted className="mb-1">
          {description}
        </Text>
      )}
      <Input
        value={typeof config[fieldKey] === "string" ? config[fieldKey] : ""}
        onChange={(val) => onChange({ ...config, [fieldKey]: val })}
        placeholder={typeof prop.default === "string" ? prop.default : ""}
      />
    </div>
  );
}

const SLACK_REPLY_MODE_FIELD = "routing";

// The reply mode decides messages and mentions, so they leave the extra event
// types once one is picked.
function withoutConversationEventTypes(eventTypes: unknown): string[] {
  if (!Array.isArray(eventTypes)) return [];
  return eventTypes.filter(
    (t): t is string =>
      typeof t === "string" && !SLACK_CONVERSATION_EVENT_TYPES.includes(t),
  );
}

// Once a reply mode is set it decides messages and mentions, so the extra
// event types stop offering them.
function withoutConversationEventOptions(
  properties: Record<string, TriggerConfigSchemaProperty>,
  config: TriggerConfig,
): Record<string, TriggerConfigSchemaProperty> {
  const eventTypes = properties.event_types;
  if (!isSlackReplyMode(config[SLACK_REPLY_MODE_FIELD]) || !eventTypes?.items) {
    return properties;
  }
  return {
    ...properties,
    event_types: {
      ...eventTypes,
      items: {
        ...eventTypes.items,
        enum: eventTypes.items.enum?.filter(
          (t) => !SLACK_CONVERSATION_EVENT_TYPES.includes(t),
        ),
      },
    },
  };
}

function SlackReplyModeField({
  config,
  onChange,
}: {
  config: TriggerConfig;
  onChange: (config: TriggerConfig) => void;
}) {
  const current = config[SLACK_REPLY_MODE_FIELD];
  return (
    <div>
      <Text variant="body" className="mb-1 font-medium">
        When should it reply?
      </Text>
      {!isSlackReplyMode(current) && (
        <Text small muted className="mb-2">
          Not set: the event types below decide which messages wake it up.
        </Text>
      )}
      <SlackReplyModeOptions
        idPrefix="trigger-slack-reply"
        value={isSlackReplyMode(current) ? current : undefined}
        onChange={(mode) =>
          onChange({
            ...config,
            [SLACK_REPLY_MODE_FIELD]: mode,
            event_types: withoutConversationEventTypes(config.event_types),
          })
        }
      />
    </div>
  );
}

function TriggerConfigFields({
  definition,
  config,
  onChange,
}: {
  definition: TriggerDefinition;
  config: TriggerConfig;
  onChange: (config: TriggerConfig) => void;
}) {
  let schema: TriggerConfigSchema;
  try {
    schema = JSON.parse(definition.configSchema) as TriggerConfigSchema;
  } catch {
    return (
      <Text small muted>
        Unable to parse config schema.
      </Text>
    );
  }

  // The Slack reply mode gets its own plain-language picker instead of the
  // generic field rendering.
  const { [SLACK_REPLY_MODE_FIELD]: slackReplyModeProp, ...otherProperties } =
    schema.properties ?? {};
  const showSlackReplyMode =
    definition.slug === "slack" && slackReplyModeProp !== undefined;
  const properties = showSlackReplyMode
    ? withoutConversationEventOptions(otherProperties, config)
    : (schema.properties ?? {});
  const required: string[] = schema.required ?? [];

  const requiredEntries = Object.entries(properties).filter(([key]) =>
    required.includes(key),
  );
  const optionalEntries = Object.entries(properties).filter(
    ([key]) => !required.includes(key),
  );

  return (
    <Stack gap={3}>
      {showSlackReplyMode && (
        <SlackReplyModeField config={config} onChange={onChange} />
      )}
      {requiredEntries.map(([key, prop]) => (
        <ConfigField
          key={key}
          fieldKey={key}
          prop={prop}
          isRequired
          config={config}
          onChange={onChange}
        />
      ))}
      {optionalEntries.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors [&[data-state=open]>svg]:rotate-90">
            <Icon
              name="chevron-right"
              className="h-4 w-4 transition-transform"
            />
            Advanced
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Stack gap={3} className="pt-3">
              {optionalEntries.map(([key, prop]) => (
                <ConfigField
                  key={key}
                  fieldKey={key}
                  prop={prop}
                  isRequired={false}
                  config={config}
                  onChange={onChange}
                />
              ))}
            </Stack>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Stack>
  );
}

function TriggerDialog({
  open,
  onOpenChange,
  editingTrigger,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingTrigger: TriggerInstance | null;
}) {
  const routes = useRoutes();
  const queryClient = useQueryClient();
  const { data: definitionsData } = useTriggerDefinitions(
    undefined,
    undefined,
    {
      retry: false,
      throwOnError: false,
    },
  );
  const createMutation = useCreateTriggerMutation();
  const updateMutation = useUpdateTriggerMutation();
  const deleteMutation = useDeleteTriggerMutation();

  const definitions = definitionsData?.definitions ?? [];

  const { data: environmentsData } = useListEnvironments();
  const environments = environmentsData?.environments ?? [];

  const [name, setName] = useState("");
  const [definitionSlug, setDefinitionSlug] = useState("");
  const [environmentId, setEnvironmentId] = useState("");
  const [config, setConfig] = useState<TriggerConfig>({});
  const [targetKind, setTargetKind] = useState<TriggerTargetKindValue>();
  const [targetDisplay, setTargetDisplay] = useState("");
  const [targetRef, setTargetRef] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEditing = editingTrigger !== null;
  const selectedDefinition = definitions.find((d) => d.slug === definitionSlug);

  const populateFromTrigger = (trigger: TriggerInstance) => {
    setName(trigger.name);
    setDefinitionSlug(trigger.definitionSlug);
    setEnvironmentId(trigger.environmentId ?? "");
    setConfig(trigger.config);
    setTargetKind(
      isTriggerTargetKind(trigger.targetKind) ? trigger.targetKind : undefined,
    );
    setTargetDisplay(trigger.targetDisplay);
    setTargetRef(trigger.targetRef);
    setConfirmDelete(false);
  };

  const reset = () => {
    setName("");
    setDefinitionSlug("");
    setEnvironmentId("");
    setConfig({});
    setTargetKind(undefined);
    setTargetDisplay("");
    setTargetRef("");
    setConfirmDelete(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  // Populate form when editing
  const prevEditId = useState<string | null>(null);
  if (editingTrigger && editingTrigger.id !== prevEditId[0]) {
    prevEditId[1](editingTrigger.id);
    populateFromTrigger(editingTrigger);
  }
  if (!editingTrigger && prevEditId[0] !== null) {
    prevEditId[1](null);
  }

  const needsEnvironment =
    selectedDefinition != null &&
    selectedDefinition.envRequirements.some((r) => r.required);

  const selectedEnvironment = environments.find((e) => e.id === environmentId);
  const missingEnvVars =
    selectedDefinition && selectedEnvironment
      ? selectedDefinition.envRequirements
          .filter((r) => r.required)
          .filter(
            (r) => !selectedEnvironment.entries.some((e) => e.name === r.name),
          )
      : [];

  const isValid =
    name.trim().length > 0 &&
    definitionSlug.length > 0 &&
    (!needsEnvironment || environmentId.length > 0) &&
    targetKind != null &&
    targetRef.trim().length > 0 &&
    targetDisplay.trim().length > 0;

  const invalidateAll = () => {
    void invalidateAllTriggers(queryClient);
    void invalidateAllTrigger(queryClient);
  };

  const handleCreate = async () => {
    if (!targetKind) return;

    await createMutation.mutateAsync({
      request: {
        createTriggerInstanceForm: {
          name,
          definitionSlug,
          config,
          targetKind,
          targetRef,
          targetDisplay,
          ...(environmentId ? { environmentId } : {}),
        },
      },
    });
    invalidateAll();
    handleOpenChange(false);
  };

  const handleUpdate = async () => {
    if (!editingTrigger || !targetKind) return;
    await updateMutation.mutateAsync({
      request: {
        updateTriggerInstanceForm: {
          id: editingTrigger.id,
          name,
          config,
          targetKind,
          targetRef,
          targetDisplay,
          ...(environmentId ? { environmentId } : {}),
        },
      },
    });
    invalidateAll();
    handleOpenChange(false);
  };

  const handleDelete = async () => {
    if (!editingTrigger) return;
    await deleteMutation.mutateAsync({
      request: { id: editingTrigger.id },
    });
    invalidateAll();
    handleOpenChange(false);
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <Dialog.Header>
          <Dialog.Title>
            {isEditing ? "Edit Trigger" : "Create Trigger"}
          </Dialog.Title>
          <Dialog.Description>
            {isEditing
              ? "Update the trigger configuration."
              : "Choose a trigger type and configure it."}
          </Dialog.Description>
        </Dialog.Header>

        <div className="min-h-0 overflow-y-auto">
          <Stack gap={4}>
            <div>
              <Text variant="body" className="mb-1 font-medium">
                Name
              </Text>
              <Input value={name} onChange={setName} placeholder="My Trigger" />
            </div>

            {!isEditing && (
              <div>
                <Text variant="body" className="mb-1 font-medium">
                  Trigger Type
                </Text>
                <Select
                  value={definitionSlug}
                  onValueChange={(val) => {
                    setDefinitionSlug(val);
                    setConfig({});
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a trigger type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {definitions.map((def) => (
                      <SelectItem
                        key={def.slug}
                        value={def.slug}
                        description={def.description}
                      >
                        {def.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {needsEnvironment && (
              <div>
                <Text variant="body" className="mb-1 font-medium">
                  Environment
                </Text>
                <Text small muted className="mb-1">
                  This trigger type requires environment variables. Select an
                  environment that has them configured.
                </Text>
                <Select value={environmentId} onValueChange={setEnvironmentId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an environment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {environments.map((env) => (
                      <SelectItem key={env.id} value={env.id}>
                        {env.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {missingEnvVars.length > 0 && selectedEnvironment && (
              <div className="border-warning-default bg-warning-softest border p-3">
                <Text variant="body" className="mb-1 font-medium">
                  Missing environment variables
                </Text>
                <Text small className="text-warning-foreground">
                  The selected environment is missing required variables. The
                  trigger will be created but will fail at runtime until these
                  are configured in{" "}
                  <routes.environments.environment.Link
                    params={[selectedEnvironment.slug]}
                    className="font-medium underline"
                  >
                    {selectedEnvironment.name}
                  </routes.environments.environment.Link>
                  :
                </Text>
                <ul className="mt-2 space-y-1">
                  {missingEnvVars.map((req) => (
                    <li
                      key={req.name}
                      className="flex items-center gap-2 text-sm"
                    >
                      <code className="bg-muted px-1.5 py-0.5 text-xs">
                        {req.name}
                      </code>
                      {req.description && (
                        <span className="text-muted-foreground">
                          {req.description}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedDefinition && (
              <TriggerConfigFields
                definition={selectedDefinition}
                config={config}
                onChange={setConfig}
              />
            )}

            <div>
              <Text variant="body" className="mb-1 font-medium">
                Target Kind
              </Text>
              <Select
                value={targetKind}
                onValueChange={(value) =>
                  setTargetKind(isTriggerTargetKind(value) ? value : undefined)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a target kind..." />
                </SelectTrigger>
                <SelectContent>
                  {triggerTargetKindOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      description={option.description}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Text variant="body" className="mb-1 font-medium">
                Target Display Name
              </Text>
              <Input
                value={targetDisplay}
                onChange={setTargetDisplay}
                placeholder="e.g. My Assistant"
              />
            </div>

            <div>
              <Text variant="body" className="mb-1 font-medium">
                Target Reference
              </Text>
              <Input
                value={targetRef}
                onChange={setTargetRef}
                placeholder="e.g. assistant ID or slug"
              />
            </div>
          </Stack>
        </div>

        <Dialog.Footer>
          {isEditing && (
            <div className="mr-auto">
              {confirmDelete ? (
                <Stack direction="horizontal" gap={2}>
                  <Button
                    variant="destructive-primary"
                    onClick={() => void handleDelete()}
                    disabled={isPending}
                  >
                    {deleteMutation.isPending
                      ? "Deleting..."
                      : "Confirm Delete"}
                  </Button>
                  <Button
                    variant="tertiary"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="tertiary"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Button.LeftIcon>
                    <Icon name="trash-2" className="h-4 w-4" />
                  </Button.LeftIcon>
                  <Button.Text>Delete</Button.Text>
                </Button>
              )}
            </div>
          )}
          <Button variant="tertiary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void (isEditing ? handleUpdate() : handleCreate())}
            disabled={!isValid || isPending}
          >
            {isPending
              ? isEditing
                ? "Saving..."
                : "Creating..."
              : isEditing
                ? "Save Changes"
                : "Create Trigger"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

/**
 * Triggers list, dialog, and empty/loading states without a surrounding
 * `Page` shell. Rendered standalone by the `/triggers` route (`TriggersIndex`)
 * and embedded as a tab on the Assistants page.
 */
export function TriggersPanel(): JSX.Element {
  const project = useProject();
  const { hasScope } = useRBAC();
  const canWrite = hasScope("project:write", project.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<TriggerInstance | null>(
    null,
  );

  const { data: triggersData, isLoading: triggersLoading } = useTriggers(
    undefined,
    undefined,
    { retry: false, throwOnError: false },
  );
  const { data: definitionsData, isLoading: definitionsLoading } =
    useTriggerDefinitions(undefined, undefined, {
      retry: false,
      throwOnError: false,
    });

  const triggers = triggersData?.triggers ?? [];
  const definitions = definitionsData?.definitions ?? [];
  const isLoading = triggersLoading || definitionsLoading;

  const openCreate = () => {
    setEditingTrigger(null);
    setDialogOpen(true);
  };

  const openEdit = (trigger: TriggerInstance) => {
    setEditingTrigger(trigger);
    setDialogOpen(true);
  };

  return (
    <>
      <Page.Section>
        <Page.Section.Title>Triggers</Page.Section.Title>
        <Page.Section.Description>
          Connect external events to your assistants via webhooks or cron
          schedules.
        </Page.Section.Description>
        <Page.Section.CTA>
          {triggers.length > 0 && (
            <RequireScope
              scope="project:write"
              resourceId={project.id}
              level="component"
              reason="You don't have permission to create triggers."
            >
              <Button onClick={openCreate}>
                <Button.LeftIcon>
                  <Icon name="plus" className="h-4 w-4" />
                </Button.LeftIcon>
                <Button.Text>Create Trigger</Button.Text>
              </Button>
            </RequireScope>
          )}
        </Page.Section.CTA>
        <Page.Section.Body>
          {isLoading ? (
            <Stack align="center" justify="center" className="py-16">
              <Icon
                name="loader-circle"
                className="text-muted-foreground h-6 w-6 animate-spin"
              />
            </Stack>
          ) : triggers.length === 0 ? (
            <TriggersEmptyState onCreate={openCreate} projectId={project.id} />
          ) : (
            <TriggersTable
              triggers={triggers}
              definitions={definitions}
              onEdit={canWrite ? openEdit : undefined}
            />
          )}
        </Page.Section.Body>
      </Page.Section>

      {canWrite && (
        <TriggerDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingTrigger(null);
          }}
          editingTrigger={editingTrigger}
        />
      )}
    </>
  );
}

export default function TriggersIndex(): JSX.Element {
  const project = useProject();
  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs />
      </Page.Header>
      <Page.Body>
        <RequireScope scope="project:read" resourceId={project.id} level="page">
          <TriggersPanel />
        </RequireScope>
      </Page.Body>
    </Page>
  );
}
